import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { useSeat } from './seat';
import { AddClientNote } from './AddClientNote';
import { ClientPurchases } from './Clients';
import { SmsReminders } from './SmsReminders';
import { formatClock, formatDayLong, formatMoney, isoDayIn } from '../mirror/time';
import type { Appointment, ClientDetail } from '../wire';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';

/**
 * **The appointment tap** (B1; `docs/lovespa-discovery.md` §3).
 *
 * > "Tapping an appointment today shows the name and the appointment note only:
 * > **no last visit, no preferences, no allergies**."
 *
 * That was the audit line. This is what a tap opens instead: who is coming, what
 * she had last time, what she must not be given, what the owner wrote down about
 * her, whether it is nearly her birthday, how long this one takes *her*, and one
 * tap to add a note while the thought is still there.
 *
 * ## Everything on it comes out of the mirror
 *
 * Not one fetch. The appointment is a mirrored row, and the client's notes,
 * preferences, birthday and remembered lengths ride on the mirrored `clients`
 * row — which is why the hydrate asks for details rather than summaries
 * (`mirror.refreshClients`). A sheet that needed a connection would be blank in
 * exactly the room where it is read: a treatment room, on a phone, with the door
 * shut.
 *
 * **Last visit** is the one thing that could have needed a read, and it does
 * not: `GET /v1/clients/{id}/visits` exists and would have been a network call
 * per tap, while the mirror already holds a window of the book on either side of
 * today. So it is computed locally, from the most recent *completed* appointment
 * for this client that this device has — and when the device holds nothing older
 * than its window, the sheet says "nothing in the last few weeks" rather than
 * "never", because those are different claims and only one of them is true.
 *
 * The one exception is **Purchases** (B3), and it is behind a tap rather than on
 * the sheet for exactly that reason: it is the only thing here that goes to the
 * network, so opening an appointment never waits on one.
 *
 * ## Allergies are not a badge in a list
 *
 * They are lifted out, above the preferences, and marked. A therapist scanning a
 * sheet for the one line that matters should not have to read a mixed list to
 * find it — `docs/lovespa-discovery.md` §3 puts *"can't use the strawberry on
 * her"* in the owner's own mouth, and the cost of missing it is a reaction on
 * somebody's face.
 *
 * ## Two of its controls write, and they are drawn for `can_write` only
 *
 * The note box (`AddClientNote`) and the reminder consent (`SmsReminders`) are
 * intents; everything else here is a read. A `readonly` seat and the console's
 * support look open the same sheet — who is coming, what she must not be
 * given — and see neither control (L15, `seat.tsx`): `core-api` refuses both
 * writes for such a seat, and a box that files a note into a refusal is worse
 * than no box. Purchases stays: it is a read.
 */
export function AppointmentSheet({
  appointment,
  onClose,
}: {
  appointment: Appointment;
  onClose: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();

  const { session, canWrite } = useSeat();
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';
  const canSell = (session?.modules ?? []).includes('sales');
  const clientId = appointment.client.client_id;
  /**
   * B3, reached from B1 — **on request, not on open**.
   *
   * The purchases list is the one thing on this sheet that needs a network
   * (`ClientPurchases` argues why), so it is a disclosure rather than a section:
   * a tap on an appointment in a treatment room must not fire a request and wait
   * for it. Opened, it is the same component the client record draws, from the
   * same read — a second list of somebody's purchases would eventually disagree
   * with the first.
   */
  const [showPurchases, setShowPurchases] = useState(false);

  const client = useLive(async () => (await runtime.mirror.client(clientId)) ?? null, null, [
    clientId,
  ]);
  const history = useLive(() => runtime.mirror.visits(clientId), [], [clientId]);

  const lastVisit = lastCompletedBefore(history, appointment.appointment_id);
  const allergies = (client?.preferences ?? []).filter((p) => p.category === 'allergy');
  const preferences = (client?.preferences ?? []).filter((p) => p.category !== 'allergy');
  // `?? []` — a row this device wrote before the field existed comes back
  // without it whatever the type says (`wire.ts`, `ClientList`).
  const remembered = (client?.durations ?? []).find((d) => d.service_id === appointment.service_id);
  const birthday = birthdayNote(client?.birthday ?? null, timezone, runtime.now(), t);

  return (
    <div className="sheet" role="dialog" aria-label={t('visit.dialogLabel')}>
      <div className="sheet__panel">
        <header>
          <h2>{appointment.client.display_name}</h2>
          <p className="muted">
            {t('visit.headline', {
              clock: formatClock(appointment.slot.start, timezone, locale),
              // **Which way it was sold** (migration 0048): "Custom Deluxe
              // Facial · 90 minutes". Without it a therapist reads the same
              // sentence for the thirty and the ninety, and `duration_min`
              // beside it is the *whole* span including anything attached —
              // two numbers that do not have to match and do not explain
              // themselves.
              service:
                appointment.option_name === null
                  ? appointment.service_name
                  : t('visit.serviceOption', {
                      service: appointment.service_name,
                      option: appointment.option_name,
                    }),
              minutes: appointment.duration_min,
            })}
          </p>
        </header>

        {/*
         * **The add-ons, as their own lines** — because that is what they are
         * on the book (`appointment_services`), and because the till will ring
         * them up as separate lines in a moment. The numbers are the snapshot
         * taken when it was booked, which is what the guest was quoted.
         *
         * `?? []` for the mirror's reason: a row this device wrote before the
         * field existed comes back without it whatever the type says.
         */}
        {(appointment.add_ons ?? []).length === 0 ? null : (
          <>
            <h3>{t('visit.addOns')}</h3>
            <ul className="add-on-list">
              {(appointment.add_ons ?? []).map((a, index) => (
                <li key={a.service_id ?? `${a.name}-${String(index)}`}>
                  <strong>{a.name}</strong>
                  <small>
                    {t('visit.addOnLine', {
                      price: formatMoney(a.price_cents, currency, locale),
                      minutes: a.duration_min ?? 0,
                    })}
                  </small>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Loudest first, and deliberately so. */}
        {allergies.length > 0 ? (
          <section className="allergies" aria-labelledby="visit-allergies">
            <h3 id="visit-allergies">{t('visit.allergies')}</h3>
            <ul>
              {allergies.map((a) => (
                <li key={a.preference_id}>
                  <strong>{a.label}</strong>
                  {a.detail === null || a.detail === '' ? null : <small> — {a.detail}</small>}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {birthday === null ? null : <p className="birthday">{birthday}</p>}

        <h3>{t('visit.lastVisit')}</h3>
        <p>
          {lastVisit === undefined
            ? t('visit.noRecentVisit')
            : t('visit.lastVisitLine', {
                day: formatDayLong(isoDayIn(timezone, lastVisit.slot.start), timezone, locale),
                service: lastVisit.service_name,
              })}
        </p>

        {preferences.length === 0 ? null : (
          <>
            <h3>{t('visit.preferences')}</h3>
            <ul className="preference-list">
              {preferences.map((p) => (
                <li key={p.preference_id} className={p.internal ? 'is-internal' : ''}>
                  <strong>{p.label}</strong>
                  {p.detail === null || p.detail === '' ? null : <small>{p.detail}</small>}
                </li>
              ))}
            </ul>
          </>
        )}

        {remembered === undefined ? null : (
          <p className="muted">
            {t('visit.herLength', {
              service: appointment.service_name,
              minutes: remembered.duration_min,
              catalog: remembered.catalog_duration_min,
            })}
          </p>
        )}

        <h3>{t('visit.notes')}</h3>
        {/* The appointment's own note and the client's are different facts and
            are never merged: one is about today, the other is about her. */}
        {appointment.notes === null || appointment.notes === '' ? null : (
          <p className="visit-note">{appointment.notes}</p>
        )}
        {client?.notes === null || client?.notes === undefined || client.notes === '' ? (
          <p className="muted">{t('visit.noNotes')}</p>
        ) : (
          <pre className="client-notes">{client.notes}</pre>
        )}

        {canWrite ? (
          <AddClientNote
            clientId={clientId}
            clientName={appointment.client.display_name}
            existingNotes={client?.notes ?? ''}
          />
        ) : null}

        {/* The second place the one consent control is drawn, and the reason
            there are two: this sheet is open while she is *in the room*, which
            is the only moment "is it OK if we text you a reminder?" is a
            question somebody can actually ask. Mirrored like everything else
            here, so it works with the door shut — `null` only until the
            mirrored row arrives, never because there is no signal. */}
        {client === null || !canWrite ? null : <SmsReminders client={client} />}

        {canSell && showPurchases ? (
          <ClientPurchases clientId={clientId} currency={currency} />
        ) : null}

        <div className="sheet__actions">
          {canSell && !showPurchases ? (
            <button type="button" className="link-button" onClick={() => setShowPurchases(true)}>
              {t('visit.purchases')}
            </button>
          ) : null}
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The most recent visit this device can see, not counting the one being looked
 * at.
 *
 * `completed` only. A cancellation and a no-show are things that did not happen,
 * and "last time she had the signature facial" must not be a facial she never
 * received. A future confirmed booking is likewise not a visit.
 */
export function lastCompletedBefore<T extends Appointment>(
  history: readonly T[],
  exceptId: string,
): T | undefined {
  // `mirror.visits` already sorts newest first; sorting again here would be a
  // second definition of "most recent" for the same list.
  return history.find((a) => a.appointment_id !== exceptId && a.status === 'completed');
}

/**
 * "birthday today" / "birthday tomorrow" / "birthday in N days", or nothing.
 *
 * The comparison is **month-and-day in the tenant's timezone** — the year in
 * `clients.birthday` is when she was born and is none of this function's
 * business — and a 29 February birthday reads as 28 February in a year that has
 * no 29th, which is the convention that greets somebody on a date that exists.
 *
 * A week ahead, because that is the window in which knowing is useful: the
 * appointment in front of you may be the last one before it. The Today screen's
 * own line (B2) is narrower — today and tomorrow only — and both read the same
 * `daysUntilBirthday`, so the two can never disagree about whose birthday it is.
 */
export function birthdayNote(
  birthday: string | null,
  timezone: string,
  nowMs: number,
  t: T,
): string | null {
  const days = daysUntilBirthday(birthday, timezone, nowMs);
  if (days === null) return null;
  if (days === 0) return t('visit.birthdayToday');
  if (days === 1) return t('visit.birthdayTomorrow');
  return t('visit.birthdaySoon', { days });
}

/**
 * How many days from today (in the tenant's timezone) to the next occurrence of
 * this birthday, or `null` when there is no usable date.
 *
 * Returns `0`..`7` and `null` for everything else, including a birthday that has
 * just passed: "it was her birthday on Tuesday" is a different sentence and not
 * one this asked for.
 *
 * `clients.birthday` is free text with a documented `YYYY-MM-DD` shape, so a row
 * that is not that shape answers `null` rather than being guessed at. A birthday
 * imported as `1984-02-29` in a non-leap year resolves to the 28th.
 */
export function daysUntilBirthday(
  birthday: string | null,
  timezone: string,
  nowMs: number,
): number | null {
  if (birthday === null) return null;
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthday.trim());
  if (parsed === null) return null;
  const month = Number(parsed[2]);
  const dayOfMonth = Number(parsed[3]);
  if (month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) return null;

  const today = isoDayIn(timezone, nowMs);

  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = addDaysToIsoDay(today, offset);
    const [cy, cm, cd] = candidate.split('-').map(Number) as [number, number, number];
    if (cm === month && cd === dayOfMonth) return offset;
    // 29 February in a year that has none: greet her on the 28th. Checked on the
    // candidate rather than on the birthday so it costs nothing in a leap year,
    // where the 29th exists and matches above.
    if (month === 2 && dayOfMonth === 29 && cm === 2 && cd === 28 && !isLeapYear(cy)) {
      return offset;
    }
  }
  return null;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Calendar-day arithmetic on a `YYYY-MM-DD`, done at noon UTC.
 *
 * The same trick `mirror/time.ts` uses: a date with no time is not an instant,
 * and midday is far enough from either boundary that no offset can move the
 * day.
 */
function addDaysToIsoDay(isoDay: string, days: number): string {
  const at = new Date(`${isoDay}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** Clients whose birthday falls within the window, nearest first. */
export function birthdaysWithin(
  clients: readonly ClientDetail[],
  timezone: string,
  nowMs: number,
  withinDays: number,
): { client: ClientDetail; days: number }[] {
  return clients
    .filter((c) => !c.archived)
    .map((client) => ({ client, days: daysUntilBirthday(client.birthday, timezone, nowMs) }))
    .filter((row): row is { client: ClientDetail; days: number } => row.days !== null)
    .filter((row) => row.days <= withinDays)
    .sort((a, b) => a.days - b.days || a.client.display_name.localeCompare(b.client.display_name));
}
